import { General } from "../../siteManagement/general.js";
import { Sheet } from "../../siteManagement/sheet.js";
import { StringKeyDictionary } from "../commonInterfaces.js";
import { FullHeightContainer } from "../core/components/FullHeighContainer.js";
import { BreakPoint, GridLayoutConfig, GridLayoutItem, SimpleGridLayout } from "../core/components/gridLayout.js";
import { KendoTree } from "../core/components/kendoTree.js";
import { Region } from "../core/components/region.js";
import { SkillEditBoxProps } from "../core/components/skills/skillEditBox.js";
import { GridCustomToolBarButton, ToolBarStandardButton, ToolBarStandardButtonType } from "../core/components/toolBar.js";
import { Config } from "../core/config/config.js";
import { DialogHelper } from "../core/util/dialogHelper.js";
import { Helper } from "../core/util/helper.js";
import { HTTPService, JsonResultClass } from "../core/util/httpManager.js";
import { NotificationHelper } from "../core/util/notificationHelper.js";
import { Skill, SkillCategory, SkillCategoryVm, SkillFilter, TreeNodeVm } from "../generalSettings/skills/skillsInterfaces.js";
import { SkillTreeFilterApp } from "./skillFilterApp.js";
import Skills from "./skills.js";
import React from "react";
export interface SkillTreeAppProps extends React.PropsWithChildren {
    skillSettings?: SkillSettings;
    onChange?: (dataItem, skills) => void;
    skillFilter: SkillFilter;
    skillEditBoxProps?: SkillEditBoxProps;
    initialSkills?: SkillCategory[];
    onDataLoaded?: (data: TreeNodeVm[]) => void;
    onDataChanged?: (data: TreeNodeVm[]) => void;
    loadOnDemand?: boolean;
    disabled?: boolean;
    renderWithRegions?: boolean;
}

interface SkillTreeAppState {
    treeData: TreeNodeVm[];
    selectedNode?: TreeNodeVm;
    useTargetValues?: boolean;
    usePriority?: boolean;
    columnCount?: number;
    skillFilter: SkillFilter;
}

export interface SkillSettings {
    useOgrNr?: boolean;
    useTargetValues?: boolean;
    usePriority?: boolean;

}

export class SkillTreeApp extends React.Component<SkillTreeAppProps, SkillTreeAppState>{
    private treeRef: KendoTree;
    private sheet: Sheet;

    constructor(props) {
        super(props);
        this.state = {
            treeData: null,
            skillFilter: this.props.skillFilter
        }
    }

    componentDidMount() {
        this.sheet = General.Sheet.Manager.getTopMostSheet();
        this.loadData();
        this.setTargetAndPriorityState();
    }

    private setTargetAndPriorityState = () => {
        let useTargetValues = this.props.skillSettings.useTargetValues;
        let usePriority = this.props.skillSettings.usePriority;
        if (Helper.hasValue(this.props.skillEditBoxProps)) {
            useTargetValues = useTargetValues && this.props.skillEditBoxProps.useTargetValues;
            usePriority = usePriority && this.props.skillEditBoxProps.usePriority;
        }

        let columnCount = this.getColumnCount(useTargetValues, usePriority);
        this.setState({ useTargetValues: useTargetValues, usePriority: usePriority, columnCount: columnCount });
    }

    private getColumnCount = (useTargetValues: boolean, usePriority: boolean) => {
        let columnCount = 2;
        columnCount = useTargetValues ? columnCount + 2 : columnCount;
        columnCount = usePriority ? columnCount + 1 : columnCount;
        return columnCount;
    }

    private filter = () => {
        if (Helper.hasValue(this.treeRef)) {
            if (this.props.loadOnDemand)
                this.loadAllDataItems();
            this.filterInternal(this.treeRef.getDataSource());
        }
    }

    private filterInternal = (dataSource) => {
        let anyVisibleChildren = false;
        let data = dataSource instanceof kendo.data.HierarchicalDataSource && dataSource.data();

        for (var i = 0; i < data.length; i++) {
            let item = data[i];

            let itemVisible = this.filterQuery(item);

            let visibleChildren = this.filterInternal(item.children);

            item.hidden = !itemVisible && !visibleChildren;
            anyVisibleChildren = anyVisibleChildren || !item.hidden;
        }

        if (data) {
            dataSource.filter({ field: "hidden", operator: "neq", value: true });
        }

        return anyVisibleChildren;
    }

    private filterQuery = (item) => {
        let itemVisible = true;
        if (item.IsSkill) {
            if (this.state.skillFilter.ShowOnlyCatsWithValues) {
                itemVisible = itemVisible && item.Item.Wert > 0;
            }
            if (Helper.hasValue(this.state.skillFilter.Text)) {
                itemVisible = itemVisible && item.Item.Rtext && item.Item.Rtext.includes(this.state.skillFilter.Text);
            }
        } else {
            if (this.state.skillFilter.ShowOnlyCatsWithSkills) {
                itemVisible = itemVisible && item.Children.filter(x => x.IsSkill).length > 0;
            }
            if (this.state.skillFilter.ShowOnlyCatsWithValues) {
                itemVisible = itemVisible && item.Children.filter(x => x.IsSkill && x.Item.Wert > 0).length > 0;
            }
            if (Helper.hasValue(this.state.skillFilter.Text)) {
                itemVisible = itemVisible && item.Item.FText && item.Item.FText.includes(this.state.skillFilter.Text);
            }
        }
        return itemVisible;
    }

    public async saveSkills() {
        let skills = this.getAllSkills();
        let result = await HTTPService.post<JsonResultClass>("EditSkill/SaveSkills?refNr=" + encodeURIComponent(this.state.skillFilter.RefNr) + "&pos=" + encodeURIComponent(this.state.skillFilter.Typ), skills);
        if (result.Code != 0) {
            NotificationHelper.showErrorMessage(result.Message);
        }
    }

    public mapCategoriesToTree(skillCategories: SkillCategory[]) {
        if (Helper.hasValue(this.treeRef)) {
            for (let skillCategory of skillCategories) {
                for (let skill of skillCategory.Skills) {
                    let dataItem: any = this.treeRef.getDataItemById(skill.Fid + "/" + skill.Rindex);
                    if (Helper.hasValue(dataItem)) {
                        let item = dataItem.Item;
                        item.Wert = skill.Wert;
                        item.SollWert = skill.SollWert;
                        item.SollDate = skill.SollDate;
                        item.Prio = skill.Prio;
                    }
                }
            }
            this.setState({ treeData: this.treeRef.getEntries() }, () => {
                this.treeRef.sync();
                this.onDataChanged();
            });
        }
    }

    public clearAllSkills = () => {
        let treeNodes = this.treeRef.getEntries();
        for (let treeNode of treeNodes) {
            this.clearCatSkills(treeNode);
        }
    }

    private callWithConfirmation = (action: () => void) => {
        DialogHelper.confirm(Config.translations.deleteMessage).then(isOk => {
            if (isOk) {
                action();
            }
        })
    }

    public getTreeNodeData() {
        return this.state.treeData;
    }

    private async loadData() {
        let result = await HTTPService.get<JsonResultClass<TreeNodeVm[]>>("EditSkill/GetSkillsTreeData", this.state.skillFilter);
        if (result.Code == 0) {
            this.setState({ treeData: result.Data }, this.onDataLoaded);
        }
        else {
            NotificationHelper.showErrorMessage(result.Message);
        }
    }

    private onDataLoaded = () => {
        if (Helper.hasValue(this.props.onDataLoaded)) {
            this.props.onDataLoaded(this.state.treeData);
        }
    }

    private onDataChanged = () => {
        if (Helper.hasValue(this.props.onDataChanged)) {
            this.props.onDataChanged(this.state.treeData);
        }
    }

    private skillsHasValue = (node: TreeNodeVm): boolean => {
        let skills = this.getSkillsRecursiv(node);
        let skillWithValues = skills.filter(x => x.Wert > 0);
        return skillWithValues.length > 0;
    }


    private skillsHasValueAll = (): boolean => {
        let minOneSkillHasValue = false;
        for (let rootNode of this.state.treeData) {
            minOneSkillHasValue = this.skillsHasValue(rootNode);
            if (minOneSkillHasValue)
                break;
        }
        return minOneSkillHasValue;
    }

    private onSkillEditBoxChange = (skill: Skill, value, field) => {
        let node: any = this.treeRef.getNodeById(skill.Fid + "/" + skill.Rindex);
        let dataNode = this.treeRef.getDataItemByNode(node);
        if (Helper.hasValue(dataNode)) {
            switch (field) {
                case "wert":
                    dataNode.set("Item.Wert", value);
                    break;
                case "sollWert":
                    dataNode.set("Item.SollWert", value);
                    break;
                case "sollDate":
                    dataNode.set("Item.SollDate", value);
                    break;
                case "prio":
                    dataNode.set("Item.Prio", value);
                    break;
            }
            //dataNode.loaded(false);
            //dataNode.load();
            let selected: any = this.treeRef.getSelected();
            this.setState({ treeData: this.treeRef.getEntries(), selectedNode: selected }, this.onDataChanged);
        }
    }

    private clearCatSkills = (node: TreeNodeVm) => {
        if (Helper.hasValue(node)) {
            let skillNodes = this.getSkillNodesRecursiv(node);
            for (let skillNode of skillNodes) {
                skillNode.Item.Wert = 0;
                this.replaceNode(skillNode, skillNode.Id);
            }
        }
        let selected: any = this.treeRef.getSelected();
        this.setState({ treeData: this.treeRef.getEntries(), selectedNode: selected }, () => {
            this.treeRef.sync();
            this.onDataChanged()
        });
    }

    private getAllSkills() {
        let allSkills: Skill[] = [];
        let treeData = this.treeRef.getEntries();
        for (let root of treeData) {
            let skills = this.getSkillsFromCategoryRecursiv(root);
            allSkills.push(...skills);
        }
        return allSkills;
    }

    private getSkillsFromCategoryRecursiv(treeNode: TreeNodeVm) {
        let allSkills: Skill[] = [];
        let skills = treeNode.Children.filter(x => x.IsSkill);
        for (let skill of skills) {
            allSkills.push(skill.Item);
        }
        let subNodes = treeNode.Children.filter(x => !x.IsSkill);
        for (let subNode of subNodes) {
            let subNodeSkills = this.getSkillsFromCategoryRecursiv(subNode);
            allSkills.push(...subNodeSkills);
        }
        return allSkills;
    }

    private getTemplate(dataItem) {
        let treeNodeItem: TreeNodeVm = dataItem.item;

        let typIcon = "";
        let text = "";
        let values = "";

        if (treeNodeItem.IsSkill) {
            let skillItem: Skill = treeNodeItem.Item;
            typIcon = `<i class='fal fa-user-graduate' title='${Helper.getTranslation("WebSMS.Skills.Skill")}' />`;
            text = skillItem.Rtext;
            if (skillItem.SkillType == 13)
                values = this.getValueElement(skillItem);
        }
        else {
            let categoryItem: SkillCategoryVm = treeNodeItem.Item;
            text = categoryItem.FText;
            typIcon = `<i class='fal fa-folder' title='${Helper.getTranslation("WebSMS.Skills.Folder")}' />`;
        }

        return `<div class='skill-tree-item__wrapper'>
                    <div>${typIcon}</div>
                    <label class='skill-tree-item__text' title='${text}'>
                        ${text}
                    </label> 
                    <div>${values}</div>
                </div>`;
    }

    private getValueElement = (skillItem: Skill) => {
        let value = Helper.hasValue(skillItem.Wert) ? skillItem.Wert.toString() : "0";
        let isValid = skillItem.Wert >= 0 && skillItem.Wert <= Config.settings.skills.maxSkillValue;
        if (this.state.useTargetValues) {
            value = value.concat("/", Helper.hasValue(skillItem.SollWert) ? skillItem.SollWert.toString() : "0");
            isValid = isValid && (skillItem.SollWert >= 0 && skillItem.SollWert <= Config.settings.skills.maxSkillValue);
        }
        return `<span class='skill-tree-item__value' data-isvalid=${isValid}>${value}</span>`;
    }

    private onSelectionChange = (dataItem: TreeNodeVm) => {
        if (Helper.hasValue(dataItem)) {
            if (this.props.loadOnDemand)
                this.loadDataItems(dataItem);
            this.setState({ selectedNode: dataItem });
        }
    }

    private loadAllDataItems = () => {
        let roots = this.treeRef.getEntries();
        for (let root of roots) {
            this.loadDataItems(root);
        }
    }

    private loadDataItems = (dataItem) => {
        if (!Helper.hasValue(dataItem) || dataItem.loaded())
            return;
        dataItem.load();
        if (Helper.hasValue(dataItem.Children) && dataItem.Children.length > 0) {
            for (let subItem of dataItem.Children) {
                if (!subItem.loaded())
                    this.loadDataItems(subItem);
            }
        }
    }

    private replaceNode(data: TreeNodeVm, id) {
        let node = this.treeRef.getNodeById(id);
        if (Helper.hasValue(node)) {
            this.treeRef.setNodeValue(node, "IsSkill", data.IsSkill);
            this.treeRef.setNodeValue(node, "Item", data.Item);
            this.treeRef.setNodeValue(node, "Children", data.Children);
        }
    }

    private afterDataSourceInit = () => {
        if (!this.props.loadOnDemand)
            this.filter();
    }

    private handleClickCustomButton = (id: string, dataItem) => {
        switch (id) {
            case "clearAllSkills":
                this.callWithConfirmation(this.clearAllSkills)
                break;
        }
    }

    private getKendoTree = () => {
        let customButtons: Array<GridCustomToolBarButton> = [
            { id: "clearAllSkills", caption: Helper.getTranslation("Skills.ClearAll"), enabled: this.skillsHasValueAll() && this.props.skillEditBoxProps.RecordRight.CanWrite, iconClass: "k-i-close", autoEnabledOnRecordSelected: false }
        ];

        let filterApp = <SkillTreeFilterApp skillFilter={this.state.skillFilter} disabled={this.props.disabled} onFilterChanged={this.onFilterChanged} />;

        return (<KendoTree
            treeData={this.state.treeData}
            template={(dataItem) => this.getTemplate(dataItem)}
            onSelectionChange={this.onSelectionChange}
            callAfterDataSourceInit={this.afterDataSourceInit}
            ref={(ref) => { this.treeRef = ref; }}
            loadOnDemand={this.props.loadOnDemand}
            showExpandAndCollapseIcons={true}
            customButtons={customButtons}
            onClickCustomButton={this.handleClickCustomButton}
            filterApp={filterApp}
            autoSelectFirstElement={true}
        />);
    }

    private handleClickStandardButtonSkills = (type: ToolBarStandardButtonType) => {
        switch (type) {
            case ToolBarStandardButtonType.DELETE:
                this.callWithConfirmation(() => this.clearCatSkills(this.state.selectedNode));
                break;
        }
    }

    private getSkillElement() {
        if (!Helper.hasValue(this.state.selectedNode))
            return null;

        let standardButtonsSkills: Array<ToolBarStandardButton> = [
            { type: ToolBarStandardButtonType.DELETE, caption: Helper.getTranslation("Skills.ClearCategory"), enabled: this.skillsHasValue(this.state.selectedNode) && this.props.skillEditBoxProps.RecordRight.CanWrite }
        ];

        return <Skills
            getSkillNodesRecursive={this.getSkillNodesRecursiv}
            selectedNode={this.state.selectedNode}
            columnCount={this.state.columnCount}
            usePriority={this.state.usePriority}
            useTargetValues={this.state.useTargetValues}
            skillEditBoxProps={this.props.skillEditBoxProps}
            onSkillEditBoxChange={this.onSkillEditBoxChange}
            standardButtons={standardButtonsSkills}
            onClickStandardButton={this.handleClickStandardButtonSkills}
        />;
    }

    private getSkillsRecursiv(treeNode: TreeNodeVm): Skill[] {
        let skillNodes = this.getSkillNodesRecursiv(treeNode);
        let skills: Skill[] = [];
        for (let skillNode of skillNodes) {
            skills.push(skillNode.Item);
        }
        return skills;
    }

    private getSkillNodesRecursiv = (treeNode: TreeNodeVm): TreeNodeVm[] => {
        let skills: TreeNodeVm[] = [];

        let skillNodes = treeNode.Children.filter(x => x.IsSkill && this.matchFilter(x.Item));
        skills.push(...skillNodes);

        let categoryNodes = treeNode.Children.filter(x => !x.IsSkill);
        for (let categoryNode of categoryNodes) {
            skills.push(...this.getSkillNodesRecursiv(categoryNode));
        }
        return skills;
    }

    private matchFilter = (skill): boolean => {
        return skill.Rtext?.toLowerCase().includes(this.state.skillFilter.Text?.toLowerCase())
            && (!this.state.skillFilter.ShowOnlyCatsWithValues || skill.Wert > 0);
    }

    private configCategories: GridLayoutConfig = [
        {
            breakPoint: BreakPoint.ExtraSmall,
            size: "12/12"
        },
        {
            breakPoint: BreakPoint.Small,
            size: "4/12"
        },
        {
            breakPoint: BreakPoint.Medium,
            size: "4/12"
        },
        {
            breakPoint: BreakPoint.Large,
            size: "4/12"
        },
        {
            breakPoint: BreakPoint.ExtraLarge,
            size: "4/12"
        }
    ];

    private configSkills: GridLayoutConfig = [
        {
            breakPoint: BreakPoint.ExtraSmall,
            size: "12/12"
        },
        {
            breakPoint: BreakPoint.Small,
            size: Config.settings.isMobileApp ? "12/12" : "8/12"
        },
        {
            breakPoint: BreakPoint.Medium,
            size: Config.settings.isMobileApp ? "12/12" : "8/12"
        },
        {
            breakPoint: BreakPoint.Large,
            size: Config.settings.isMobileApp ? "12/12" : "8/12"
        },
        {
            breakPoint: BreakPoint.ExtraLarge,
            size: Config.settings.isMobileApp ? "12/12" : "8/12"
        }
    ];

    private onFilterChanged = (filter: SkillFilter) => {
        let skillFilter = this.state.skillFilter;
        skillFilter.ShowOnlyCatsWithSkills = filter.ShowOnlyCatsWithSkills;
        skillFilter.ShowOnlyCatsWithValues = filter.ShowOnlyCatsWithValues;
        skillFilter.Text = filter.Text;
        this.setState({ skillFilter: skillFilter }, this.filter);
    }

    render() {
        let tree = Helper.hasValue(this.state.treeData) ? this.getKendoTree() : null;
        let skills = Helper.hasValue(this.state.selectedNode) ? this.getSkillElement() : null;
        if (!this.props.renderWithRegions)
            return <div style={{ display: "flex", gap: "2em", flexWrap: "wrap" }}>
                <div style={{ height: "100%" }}>{tree}</div>
                <div style={{ height: "100%", overflowX: "hidden" }}>{skills}</div>
            </div>
        return (
            <FullHeightContainer>
                <SimpleGridLayout>
                    <GridLayoutItem layoutConfig={this.configCategories} key={"skillTreeGridLayout"}>
                        <Region title={Helper.getTranslation("Skills.Category")} renderCollapsed={false} id="skillTreeRegion">
                            {tree}
                        </Region>
                    </GridLayoutItem>
                    <GridLayoutItem layoutConfig={this.configSkills} key={"skillsGridLayout"}>
                        <Region title={Helper.getTranslation("Skills.Skill")} renderCollapsed={false}>
                            {skills}
                        </Region>
                    </GridLayoutItem>
                </SimpleGridLayout>
            </FullHeightContainer>);
    }
}